import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Gutachtenart, RECHNUNGSPOSITION_EINHEIT_AUSWAHL } from '../shared/constants';
import { Mandant, VorgangRechnungspositionEintrag } from '../types';
import { EuroFormat } from './common/inputFormats';
import Bestaetigungsdialog from './dialog/Bestaetigungsdialog';
import { ermittleRechnungspositionLabel } from '../shared/rechnungHelper';

const filterOptions = createFilterOptions<string>({
  matchFrom: 'start',
  trim: true
});

type Props = {
  readonly reihenfolgeAenderbar: boolean;
  readonly rechnungsparameter: VorgangRechnungspositionEintrag[];
  readonly setRechnungsparameter: (rechnungspositionen: VorgangRechnungspositionEintrag[]) => void;
  readonly mandant: Mandant | null;
  readonly gutachtenart: Gutachtenart;
  readonly positionenAenderbar: boolean;
  readonly positionenLoeschbar: boolean;
};

export function RechnungspositionenTabelle({
  reihenfolgeAenderbar,
  rechnungsparameter,
  setRechnungsparameter,
  mandant,
  gutachtenart,
  positionenAenderbar,
  positionenLoeschbar
}: Props) {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [loeschenIndex, setLoeschenIndex] = useState<number>(-1);

  const sensors = useSensors(useSensor(PointerSensor));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = parseInt(String(active.id));
      const newIndex = parseInt(String(over.id));
      setRechnungsparameter(arrayMove(rechnungsparameter, oldIndex, newIndex));
    }
  };

  const aktualisiereParameter = (index: number, attribute: keyof VorgangRechnungspositionEintrag, wert: number | string | string[] | null) => {
    const rechnungsparameterUpdate = rechnungsparameter.map((r, i) => {
      let summe = r.summe;
      if (typeof wert === 'number') {
        if (attribute === 'anzahl') {
          summe = r.wert * wert;
        }
        if (attribute === 'wert') {
          summe = r.anzahl * wert;
        }
      }
      return index === i ? { ...r, [attribute]: wert, summe, editiert: (r.editiert ?? wert !== r[attribute]) || summe !== r.summe } : r;
    });
    setRechnungsparameter(rechnungsparameterUpdate);
  };

  const loescheEintrag = (index: number) => {
    if (positionenAenderbar) {
      setRechnungsparameter(rechnungsparameter.filter((r, i) => i !== index));
    } else {
      setRechnungsparameter(rechnungsparameter.map((r, i) => (i === index ? { ...r, geloescht: true } : r)));
    }
    setLoeschenIndex(-1);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={'300px'}>Rechnungsposition</TableCell>
              <TableCell width={'100px'}>Anzahl</TableCell>
              <TableCell width={'150px'}>Einheit</TableCell>
              <TableCell width={'100px'}>Wert</TableCell>
              <TableCell width={'150px'}>Summe</TableCell>
              <TableCell width={'100px'} />
            </TableRow>
          </TableHead>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={rechnungsparameter.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
              <TableBody>
                {rechnungsparameter.map((position, index) => (
                  <RechnungsparamterZeile
                    key={index}
                    position={index}
                    parameter={position}
                    aktualisiereParameter={aktualisiereParameter}
                    setLoeschenIndex={setLoeschenIndex}
                    setOpenDeleteDialog={setOpenDeleteDialog}
                    mandant={mandant}
                    gutachtenart={gutachtenart}
                    reihenfolgeAenderbar={reihenfolgeAenderbar}
                    positionenLoeschbar={positionenLoeschbar}
                  />
                ))}
              </TableBody>
            </SortableContext>
          </DndContext>
        </Table>
      </TableContainer>
      <Bestaetigungsdialog
        title="Eintrag löschen"
        text="Möchten Sie den Eintrag wirklich löschen?"
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={() => {
          loescheEintrag(loeschenIndex);
          setOpenDeleteDialog(false);
        }}
      />
    </>
  );
}

type RechnungsparameterZeileProps = {
  readonly position: number;
  readonly parameter: VorgangRechnungspositionEintrag;
  readonly mandant: Mandant | null;
  readonly gutachtenart: Gutachtenart;
  readonly reihenfolgeAenderbar: boolean;
  readonly positionenLoeschbar: boolean;
  readonly aktualisiereParameter: (index: number, attribute: keyof VorgangRechnungspositionEintrag, value: string | number | string[] | null) => void;
  readonly setLoeschenIndex: (index: number) => void;
  readonly setOpenDeleteDialog: (open: boolean) => void;
};

function RechnungsparamterZeile({
  position,
  parameter,
  reihenfolgeAenderbar,
  aktualisiereParameter,
  setLoeschenIndex,
  setOpenDeleteDialog,
  mandant,
  gutachtenart,
  positionenLoeschbar
}: RechnungsparameterZeileProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: String(position) });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const auswahl: string[] =
    mandant?.rechnungsparameter?.filter((rp) => rp.gutachtenart.includes(gutachtenart)).map((rp) => ermittleRechnungspositionLabel(rp.titel)) ?? [];
  let className = '';
  if (parameter.geloescht) {
    className = 'deletedRow';
  } else if (parameter.editiert) {
    className = 'editedRow';
  }

  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`entry-${position}`} className={className}>
      <TableCell>
        <Autocomplete
          disabled={false}
          filterOptions={filterOptions}
          freeSolo
          options={auswahl}
          value={ermittleRechnungspositionLabel(parameter.titel)}
          onChange={(event, value: string | null) => {
            aktualisiereParameter(position, 'titel', value);
          }}
          renderInput={(params) => (
            <TextField
              variant="standard"
              {...params}
              data-testid="Rechnungsparameter-position"
              InputProps={{
                ...params.InputProps
              }}
              onChange={(event) => {
                aktualisiereParameter(position, 'titel', event.target.value);
              }}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          variant="standard"
          value={parameter.anzahl}
          data-testid="Rechnungsparameter-anzahl"
          onChange={(event) => {
            aktualisiereParameter(position, 'anzahl', parseFloat(event.target.value) || 0);
          }}
        />
      </TableCell>
      <TableCell>
        <Select
          fullWidth
          variant="standard"
          label="Einheit"
          value={parameter.einheit}
          disabled={false}
          onChange={(event: SelectChangeEvent<string>) => {
            aktualisiereParameter(position, 'einheit', event.target.value);
          }}
          data-testid="Rechnungsparameter-einheit"
        >
          {RECHNUNGSPOSITION_EINHEIT_AUSWAHL.map((einheit) => (
            <MenuItem key={einheit.value} value={einheit.value} data-testid={einheit.label}>
              {einheit.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          variant="standard"
          value={parameter.wert / 100}
          data-testid="Rechnungsparameter-wert"
          InputProps={{
            inputComponent: EuroFormat,
            endAdornment: <InputAdornment position="end">€</InputAdornment>
          }}
          onChange={(event) => {
            aktualisiereParameter(position, 'wert', (parseFloat(event.target.value) || 0) * 100);
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          disabled
          variant="standard"
          value={parameter.summe / 100}
          data-testid="Rechnungsparameter-summe"
          InputProps={{
            inputComponent: EuroFormat,
            endAdornment: <InputAdornment position="end">€</InputAdornment>
          }}
          onChange={(event) => {
            aktualisiereParameter(position, 'summe', (parseFloat(event.target.value) || 0) * 100);
          }}
        />
      </TableCell>
      <TableCell className="buttonCell">
        {positionenLoeschbar && (
          <IconButton
            onClick={() => {
              setOpenDeleteDialog(true);
              setLoeschenIndex(position);
            }}
            data-testid="Rechnungsparameter-eintrag-loeschen"
          >
            <DeleteIcon />
          </IconButton>
        )}
        {reihenfolgeAenderbar && (
          <IconButton sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
            <DragIndicatorIcon />
          </IconButton>
        )}
        {parameter.editiert && (
          <Tooltip title="Position wurde manuell editiert">
            <EditIcon color="disabled" className="editIcon" />
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}
